"use client"

import { useEffect, useRef, useState } from "react"
import {
  KB_FALLBACK_SETTLE_MS,
  kbFallbackCacheKey,
  parseCachedKbResults,
  safeStorageGet,
  safeStorageRemove,
  safeStorageSet,
  shouldFetchKbFallback,
} from "@/lib/search/kb-fallback-gate"
import { loadSearchIndex } from "@/lib/search/client"
import type { SearchHit } from "@/lib/search/engine"
import { searchKbFallbackEnabled } from "@/lib/release-flags"
import { hasAiPreviewActive } from "@/lib/ai-preview"
import { track } from "@/lib/analytics"

/**
 * What glue's `/kb-results` returns: a page on this site that its semantic
 * index matched, and how well. Nothing else — no title, no snippet.
 *
 * That is the whole design. The knowledge base is written for the chatbot,
 * which READS a chunk and writes its own sentence; rendering that prose as a
 * search result published RAG scaffolding to visitors — agent-facing playbook
 * copy, model-directed ALL-CAPS emphasis, links to pages that did not contain
 * the quoted text. So the KB's job here is only to POINT AT A PAGE. The row is
 * rendered from this site's own `SearchDoc`, exactly like a lexical hit.
 */
export interface KbResult {
  url: string
  score: number
}

const CIRCUIT_KEY = "ahead-kb-fallback-unavailable"
const SESSION_ID_KEY = "ahead-kb-fallback-session"

function randomSessionId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    // `crypto.randomUUID` can throw (or be unavailable) in locked-down
    // browser contexts — fall back to a random-ish string rather than get
    // stuck unable to produce a session id (which would leave the hook
    // stuck in "loading" forever).
    return `kb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  }
}

function sessionId(): string {
  const existing = safeStorageGet(SESSION_ID_KEY)
  if (existing) return existing
  const id = randomSessionId()
  safeStorageSet(SESSION_ID_KEY, id)
  return id
}

/** Body shape validation for the `/api/kb-results` response. */
function extractResults(body: unknown): unknown[] {
  if (
    body &&
    typeof body === "object" &&
    Array.isArray((body as { results?: unknown }).results)
  ) {
    return (body as { results: unknown[] }).results
  }
  return []
}

function isKbResult(value: unknown): value is KbResult {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { url?: unknown }).url === "string" &&
    typeof (value as { score?: unknown }).score === "number"
  )
}

/**
 * Turn matched URLs into rows, using this site's own index as the authority on
 * what exists.
 *
 * A URL that resolves to no `SearchDoc` yields no row. That is the guarantee
 * the whole feature rests on — the fallback can only ever surface a page the
 * site itself knows about, so it cannot invent a destination, quote text that
 * is not on the page it links to, or outlive a page that was removed. It is
 * also why nothing here needs a same-site check or a dead-link guard: an
 * off-site or stale URL simply matches nothing.
 *
 * The cost, and it is real: a page missing from the index is unreachable here
 * even when it renders — and that is exactly a page the lexical search cannot
 * find either. Index gaps are therefore bugs to fix in
 * `scripts/build-search-index.ts`, not something to work around here.
 */
function resolveAgainstSiteIndex(
  results: ReadonlyArray<KbResult>,
  docsByUrl: Map<string, SearchHit["doc"]>
): SearchHit[] {
  const hits: SearchHit[] = []
  const seen = new Set<string>()
  for (const result of results) {
    const doc = docsByUrl.get(result.url)
    if (!doc || seen.has(doc.id)) continue
    seen.add(doc.id)
    // KB relevance, not the lexical engine's score. The two never mix in one
    // list — these render only when the lexical search returned nothing — so
    // the scales never have to be reconciled.
    hits.push({ doc, score: result.score })
  }
  return hits
}

/**
 * Stage-0 search KB fallback: on a settled zero-result query, ask glue's
 * semantic index which pages match, then render those pages as ordinary search
 * results. Session-cached per normalized query so re-typing never re-fetches;
 * one failure sets a session circuit breaker so a down glue is hit at most once
 * per session.
 */
export function useKbFallback({
  active,
  query,
  zeroResults,
  country,
  locale,
  surface,
}: {
  active: boolean
  query: string
  zeroResults: boolean
  country: string
  locale: string
  surface: "modal" | "page"
}): { hits: SearchHit[]; status: "idle" | "loading" | "done" } {
  const [hits, setHits] = useState<SearchHit[]>([])
  const [status, setStatus] = useState<"idle" | "loading" | "done">("idle")
  const shownForRef = useRef<Set<string>>(new Set())

  const enabled = searchKbFallbackEnabled() || hasAiPreviewActive()
  const trimmed = query.trim()

  useEffect(() => {
    if (!active || !enabled) {
      setHits([])
      setStatus("idle")
      return
    }
    if (!shouldFetchKbFallback({ query: trimmed, zeroResults })) {
      setHits([])
      setStatus("idle")
      return
    }

    let cancelled = false

    /**
     * The index is already in memory: the fallback only runs after a search
     * returned zero, and that search loaded it. `loadSearchIndex` memoizes per
     * (country, locale), so this is a cache read, not a fetch.
     */
    const resolve = async (results: KbResult[]): Promise<SearchHit[]> => {
      const index = await loadSearchIndex(country, locale)
      if (!index) return []
      return resolveAgainstSiteIndex(
        results,
        new Map(index.docs.map((doc) => [doc.url, doc]))
      )
    }

    const publish = (resolved: SearchHit[], cacheKey: string) => {
      if (cancelled) return
      setHits(resolved)
      setStatus("done")
      if (resolved.length > 0 && !shownForRef.current.has(cacheKey)) {
        shownForRef.current.add(cacheKey)
        track.searchKbFallbackShown({
          query: trimmed,
          surface,
          result_count: resolved.length,
          locale,
        })
      }
    }

    // Cache is checked before the circuit breaker: a cached hit must stay
    // reachable for the rest of the session even after the breaker trips. The
    // cache holds the RAW matches, not resolved rows — the index can change
    // under a session (a deploy), and re-resolving is a map lookup.
    const cacheKey = kbFallbackCacheKey(country, locale, trimmed)
    const cachedRaw = safeStorageGet(cacheKey)
    if (cachedRaw !== null) {
      const cached = parseCachedKbResults<unknown>(cachedRaw)
      if (cached !== null) {
        setStatus("loading")
        void resolve(cached.filter(isKbResult)).then((resolved) =>
          publish(resolved, cacheKey)
        )
        return () => {
          cancelled = true
        }
      }
      // Corrupt cache entry — drop it and fall through as a cache miss.
      safeStorageRemove(cacheKey)
    }

    if (safeStorageGet(CIRCUIT_KEY)) {
      setHits([])
      setStatus("idle")
      return
    }

    // Gate holds, no cache hit, breaker not tripped: this run WILL fetch
    // once the settle window elapses. Report "loading" for the whole
    // window (settle + fetch), not just once the fetch kicks off, so the
    // UI never gets to flash a confident "no results" while a lookup is
    // still pending. The effect re-runs on every keystroke (trimmed query
    // changes) while zeroResults stays true — re-asserting "loading" here
    // each time is a no-op re-render, not a flicker back to idle.
    setStatus("loading")

    const handle = window.setTimeout(() => {
      // The breaker may have tripped during the settle wait (e.g. another
      // query's fetch failed in the meantime) — re-check before fetching.
      if (safeStorageGet(CIRCUIT_KEY)) {
        if (!cancelled) setStatus("idle")
        return
      }
      fetch("/api/kb-results", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: trimmed,
          country,
          locale,
          session_id: sessionId(),
        }),
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(`kb-results ${res.status}`)
          const body: unknown = await res.json()
          return extractResults(body).filter(isKbResult)
        })
        .then(async (fetched) => {
          if (cancelled) return
          safeStorageSet(cacheKey, JSON.stringify(fetched))
          publish(await resolve(fetched), cacheKey)
        })
        .catch(() => {
          if (cancelled) return
          safeStorageSet(CIRCUIT_KEY, "1")
          setHits([])
          setStatus("done")
        })
    }, KB_FALLBACK_SETTLE_MS)

    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [active, enabled, trimmed, zeroResults, country, locale, surface])

  return { hits, status }
}
